import React, { useState, useRef } from 'react';
import './TransformationVideos.css';

const TransformationVideos = () => {
  const [playingVideo, setPlayingVideo] = useState(null);
  const videoRefs = {
    avant: useRef(null),
    apres1: useRef(null),
    apres2: useRef(null),
  };

  const handleVideoPlay = (videoId) => {
    // Pause all other videos
    Object.keys(videoRefs).forEach((key) => {
      if (key !== videoId && videoRefs[key].current) {
        videoRefs[key].current.pause();
      }
    });
    setPlayingVideo(videoId);
  };

  const handleVideoPause = () => {
    setPlayingVideo(null);
  };

  const handleVideoToggle = (videoId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const video = videoRefs[videoId].current;
    if (!video) return;

    if (video.paused) {
      // Pause all other videos
      Object.keys(videoRefs).forEach((key) => {
        if (key !== videoId && videoRefs[key].current) {
          videoRefs[key].current.pause();
        }
      });
      video.play().catch(err => console.error('Error playing video:', err));
      setPlayingVideo(videoId);
    } else {
      video.pause();
      setPlayingVideo(null);
    }
  };

  const handleTouchStart = (videoId, e) => {
    e.stopPropagation();
  };

  const handleTouchEnd = (videoId, e) => {
    e.preventDefault();
    e.stopPropagation();
    handleVideoToggle(videoId, e);
  };

  const isVideoPlaying = (videoId) => {
    return playingVideo === videoId;
  };

  const renderPlayIndicator = (videoId) => {
    const isPlaying = isVideoPlaying(videoId);
    return (
      <div className="play-indicator">
        <svg className="play-indicator-svg" viewBox="0 0 60 60" fill="none">
          <circle cx="30" cy="30" r="30" fill="rgba(0, 212, 88, 0.9)" />
          {isPlaying ? (
            // Pause icon (||)
            <>
              <rect x="22" y="18" width="5" height="24" rx="1" fill="white" />
              <rect x="33" y="18" width="5" height="24" rx="1" fill="white" />
            </>
          ) : (
            // Play icon (▶)
            <path d="M24 18L42 30L24 42V18Z" fill="white" />
          )}
        </svg>
      </div>
    );
  };

  return (
    <section className="transformation-videos-section" id="transformation">
      <div className="container">
        <div className="text-center">
          <div className="section-title">
            <h2>Création de deux appartements haut standing</h2>
            <p className="section-subtitle">
              Découvrez la métamorphose complète de cette grange en deux appartements haut standing
            </p>
          </div>
        </div>

        {/* Before Video - Large Display */}
        <div className="video-before-container">
          <div className="video-card video-card-before">
            <div className="video-label">
              <span className="label-icon">📹</span>
              <span className="label-text">Avant la Rénovation</span>
            </div>
            <div 
              className="video-wrapper" 
              onClick={(e) => handleVideoToggle('avant', e)}
              onTouchStart={(e) => handleTouchStart('avant', e)}
              onTouchEnd={(e) => handleTouchEnd('avant', e)}
            >
              <video
                ref={videoRefs.avant}
                className="transformation-video"
                controls
                controlsList="nodownload"
                onPlay={() => handleVideoPlay('avant')}
                onPause={handleVideoPause}
                onClick={(e) => {
                  // Only stop propagation if clicking on controls, not on video itself
                  if (e.target === e.currentTarget || e.target.tagName === 'VIDEO') {
                    e.stopPropagation();
                  }
                }}
                poster=""
              >
                <source src={require('../avant.mp4')} type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
              <div 
                className="video-overlay" 
                onClick={(e) => { e.stopPropagation(); handleVideoToggle('avant', e); }}
                onTouchStart={(e) => { e.stopPropagation(); }}
                onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleVideoToggle('avant', e); }}
              >
                {renderPlayIndicator('avant')}
              </div>
            </div>
          </div>
        </div>

        {/* After Videos - Two Studios Side by Side */}
        <div className="videos-after-container">
          <div className="video-card video-card-after">
            <div className="video-label">
              <span className="label-icon">✨</span>
              <span className="label-text">Appartement 1 - Après Rénovation</span>
            </div>
            <div 
              className="video-wrapper" 
              onClick={(e) => handleVideoToggle('apres1', e)}
              onTouchStart={(e) => handleTouchStart('apres1', e)}
              onTouchEnd={(e) => handleTouchEnd('apres1', e)}
            >
              <video
                ref={videoRefs.apres1}
                className="transformation-video"
                controls
                controlsList="nodownload"
                onPlay={() => handleVideoPlay('apres1')}
                onPause={handleVideoPause}
                onClick={(e) => {
                  if (e.target === e.currentTarget || e.target.tagName === 'VIDEO') {
                    e.stopPropagation();
                  }
                }}
                poster=""
              >
                <source src={require('../apres_1.mp4')} type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
              <div 
                className="video-overlay" 
                onClick={(e) => { e.stopPropagation(); handleVideoToggle('apres1', e); }}
                onTouchStart={(e) => { e.stopPropagation(); }}
                onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleVideoToggle('apres1', e); }}
              >
                {renderPlayIndicator('apres1')}
              </div>
            </div>
            <div className="video-description">
              <p>Espace optimisé avec design contemporain</p>
            </div>
          </div>

          <div className="video-card video-card-after">
            <div className="video-label">
              <span className="label-icon">🏠</span>
              <span className="label-text">Appartement 2 - Après Rénovation</span>
            </div>
            <div 
              className="video-wrapper" 
              onClick={(e) => handleVideoToggle('apres2', e)}
              onTouchStart={(e) => handleTouchStart('apres2', e)}
              onTouchEnd={(e) => handleTouchEnd('apres2', e)}
            >
              <video
                ref={videoRefs.apres2}
                className="transformation-video"
                controls
                controlsList="nodownload"
                onPlay={() => handleVideoPlay('apres2')}
                onPause={handleVideoPause}
                onClick={(e) => {
                  if (e.target === e.currentTarget || e.target.tagName === 'VIDEO') {
                    e.stopPropagation();
                  }
                }}
                poster=""
              >
                <source src={require('../apres_2.mp4')} type="video/mp4" />
                Votre navigateur ne supporte pas la lecture de vidéos.
              </video>
              <div 
                className="video-overlay" 
                onClick={(e) => { e.stopPropagation(); handleVideoToggle('apres2', e); }}
                onTouchStart={(e) => { e.stopPropagation(); }}
                onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleVideoToggle('apres2', e); }}
              >
                {renderPlayIndicator('apres2')}
              </div>
            </div>
            <div className="video-description">
              <p>Rénovation complète avec finitions de qualité</p>
            </div>
          </div>
        </div>

        {/* Transformation Stats */}
        <div className="transformation-stats">
          <div className="stat-item">
            <div className="stat-number">45</div>
            <div className="stat-label">Appartements Créés</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">100%</div>
            <div className="stat-label">Rénovation Complète</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-number">∞</div>
            <div className="stat-label">Satisfaction Client</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TransformationVideos;

